import * as z from 'zod';

export const QueryIntakeFormsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          name: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          archived: z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _updatedDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum(['_id', 'name', 'archived', '_createdDate', '_updatedDate'])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe(
      'Filter object.\nSee @API Query Language\nfor more information.\n\nFor a detailed list of supported filters, see\n@Supported Filters.'
    ),
});
export const QueryIntakeFormsResponse = z.object({
  intakeForms: z
    .array(
      z.object({
        _id: z
          .string()
          .describe(
            'Intake form ID. Identical to the [Wix Forms form ID](https://dev.wix.com/docs/api-reference/crm/forms/form-schemas/form-object).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        expirationPeriodInMonths: z
          .number()
          .int()
          .describe(
            "Expiration period in months. If not set, submissions don't expire."
          )
          .min(1)
          .max(60)
          .optional()
          .nullable(),
        archived: z
          .boolean()
          .describe(
            'Whether the intake form is archived and disabled from accepting new submissions.'
          )
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Intake form name.')
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the intake form was created in UTC datetime `YYYY-MM-DDThh:mm:ssZ` format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the intake form was updated in UTC datetime `YYYY-MM-DDThh:mm:ssZ` format.'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the intake form is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the intake form.'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const UpdateIntakeFormExpirationPeriodRequest = z.object({
  intakeFormId: z
    .string()
    .describe('Intake form ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    expirationPeriodInMonths: z
      .number()
      .int()
      .describe(
        "New expiration period in months. If not set, submissions don't expire."
      )
      .min(1)
      .max(60)
      .optional()
      .nullable(),
    formRevision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the intake form is updated.\nTo prevent conflicting changes, you must specify the current revision when updating the intake form.'
      ),
  }),
});
export const UpdateIntakeFormExpirationPeriodResponse = z.object({
  intakeForm: z
    .object({
      _id: z
        .string()
        .describe(
          'Intake form ID. Identical to the [Wix Forms form ID](https://dev.wix.com/docs/api-reference/crm/forms/form-schemas/form-object).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      expirationPeriodInMonths: z
        .number()
        .int()
        .describe(
          "Expiration period in months. If not set, submissions don't expire."
        )
        .min(1)
        .max(60)
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          'Whether the intake form is archived and disabled from accepting new submissions.'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Intake form name.')
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe(
          'Date and time the intake form was created in UTC datetime `YYYY-MM-DDThh:mm:ssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the intake form was updated in UTC datetime `YYYY-MM-DDThh:mm:ssZ` format.'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the intake form is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the intake form.'
        )
        .optional()
        .nullable(),
    })
    .describe('Updated intake form.')
    .optional(),
});
export const CreateCustomerSubmissionLinkRequest = z.object({
  intakeFormId: z
    .string()
    .describe('Intake form ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const CreateCustomerSubmissionLinkResponse = z.object({
  intakeFormUrl: z
    .string()
    .describe('Generated link URL for the intake form submission.')
    .min(1)
    .max(2048)
    .optional()
    .nullable(),
});
export const DeleteIntakeFormRequest = z.object({
  intakeFormId: z
    .string()
    .describe('Intake form ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteIntakeFormResponse = z.object({});
export const ArchiveIntakeFormRequest = z.object({
  intakeFormId: z
    .string()
    .describe('Intake form ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  formRevision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the intake form is updated.\nTo prevent conflicting changes, you must specify the current revision when updating the intake form.'
    ),
});
export const ArchiveIntakeFormResponse = z.object({
  intakeForm: z
    .object({
      _id: z
        .string()
        .describe(
          'Intake form ID. Identical to the [Wix Forms form ID](https://dev.wix.com/docs/api-reference/crm/forms/form-schemas/form-object).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      expirationPeriodInMonths: z
        .number()
        .int()
        .describe(
          "Expiration period in months. If not set, submissions don't expire."
        )
        .min(1)
        .max(60)
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          'Whether the intake form is archived and disabled from accepting new submissions.'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Intake form name.')
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe(
          'Date and time the intake form was created in UTC datetime `YYYY-MM-DDThh:mm:ssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the intake form was updated in UTC datetime `YYYY-MM-DDThh:mm:ssZ` format.'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the intake form is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the intake form.'
        )
        .optional()
        .nullable(),
    })
    .describe('Archived intake form.')
    .optional(),
});
export const UnarchiveIntakeFormRequest = z.object({
  intakeFormId: z
    .string()
    .describe('Intake form ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  formRevision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the intake form is updated.\nTo prevent conflicting changes, you must specify the current revision when updating the intake form.'
    ),
});
export const UnarchiveIntakeFormResponse = z.object({
  intakeForm: z
    .object({
      _id: z
        .string()
        .describe(
          'Intake form ID. Identical to the [Wix Forms form ID](https://dev.wix.com/docs/api-reference/crm/forms/form-schemas/form-object).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      expirationPeriodInMonths: z
        .number()
        .int()
        .describe(
          "Expiration period in months. If not set, submissions don't expire."
        )
        .min(1)
        .max(60)
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          'Whether the intake form is archived and disabled from accepting new submissions.'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Intake form name.')
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe(
          'Date and time the intake form was created in UTC datetime `YYYY-MM-DDThh:mm:ssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the intake form was updated in UTC datetime `YYYY-MM-DDThh:mm:ssZ` format.'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the intake form is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the intake form.'
        )
        .optional()
        .nullable(),
    })
    .describe('Unarchived intake form.')
    .optional(),
});
